import {
    Component,
    input,
    model,
    OnChanges,
    OnInit,
    output,
    SimpleChanges,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    ShCheckboxComponent,
    ShPaginationComponent,
    ShSpinnerComponent,
    ShTableCellComponent,
    ShTableComponent,
    ShTableHeadComponent,
    ShTableRowComponent
} from '@gbr-monorepo/gbr-shui';
import { User } from '@gbr-monorepo/interfaces';
import { TableColumn, TableFilter } from '../../interfaces';
import { TableFilterState } from '../../types';
import { TableComponentWrapperComponent } from './table-component-wrapper';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ShCheckboxComponent,
        TableComponentWrapperComponent,
        ShTableComponent,
        ShTableRowComponent,
        ShTableHeadComponent,
        ShTableCellComponent,
        ShSpinnerComponent,
        ShPaginationComponent
    ]
})
export class TableComponent implements OnInit, OnChanges {
    public pageSize = 10;

    public readonly columns = input.required<TableColumn<User>[]>();
    public readonly data = input.required<User[]>();
    public readonly selectable = input<boolean>(false);
    public readonly pagination = input<boolean>(false);
    public readonly pageSizeOptions = input<number[]>([10, 25, 50, 100]);
    public readonly page = input<number>(1);
    public readonly total = input<number>();
    public readonly mode = input<'client' | 'server'>('client');
    public readonly isLoading = input<boolean>(false, { alias: 'loading' });
    public readonly selectAllRows = model<boolean>(false);
    public readonly selectedRows = model<User[]>([]);
    public readonly style = input<string>('');
    public readonly pageChange = output<{
        page: number;
        size: number;
        filters: TableFilterState;
    }>();
    public readonly selectionChange = output<User[]>();
    public readonly selectAllChange = output<boolean>();
    public readonly rowClick = output<User>();

    protected readonly filters: { [key: string]: TableFilter } = {};
    protected openFilterMenu: string | undefined;
    protected selectAll = false;
    protected selectionKey = '';
    protected currentPage = 1;
    private dataSize = 0;

    public ngOnInit(): void {
        this.currentPage = this.page();

        const keyColumn = this.columns().find(column => column.id);

        if (this.selectable() && keyColumn === undefined) {
            console.error("[SAG Table]: Property 'selectable' requires a column with 'id' set.");
        } else if (this.selectable() && keyColumn?.selector) {
            this.selectionKey = keyColumn.selector;
        }
    }

    public ngOnChanges(_changes: SimpleChanges): void {
        this.currentPage = this.page();
    }

    private getRowValue(row: User, key: string): unknown {
        return (row as unknown as Record<string, unknown>)[key];
    }

    protected get rows(): User[] {
        if (this.mode() === 'server') {
            return this.data();
        }

        let rows = [...this.data()];

        this.dataSize = rows.length;

        if (this.pagination()) {
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            rows = rows.slice(start, end);
        }

        return rows;
    }

    protected onPageMove(event: Event): void {
        const currentPage = Number((event.target as { currentPage?: number } | null)?.currentPage ?? this.currentPage);

        if (!Number.isFinite(currentPage) || currentPage < 1 || currentPage === this.currentPage) {
            return;
        }

        this.currentPage = currentPage;
        this.pageChange.emit({
            page: this.currentPage,
            size: this.pageSize,
            filters: this.filters
        });
    }

    protected get totalSize(): number {
        return this.total() ?? this.dataSize;
    }

    protected get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalSize / this.pageSize));
    }

    protected get pageTotal(): number {
        return Math.min(this.totalSize, this.currentPage * this.pageSize);
    }

    protected toggleSelectAll(event: Event): void {
        const checked = (event.target as HTMLInputElement | null)?.checked ?? false;

        this.selectAll = checked;

        if (this.selectAll) {
            this.selectedRows.set(this.rows);
        } else {
            this.selectedRows.set([]);
        }

        this.selectionChange.emit(this.selectAll ? this.rows : []);
        this.selectAllChange.emit(this.selectAll);
    }

    protected toggleSelect(row: User): void {
        const selectedRows = [...this.selectedRows()];
        const index = selectedRows.findIndex(
            row_ => this.getRowValue(row_, this.selectionKey) === this.getRowValue(row, this.selectionKey)
        );

        if (index === -1) {
            const selectedRow = this.rows.find(
                row_ => this.getRowValue(row_, this.selectionKey) === this.getRowValue(row, this.selectionKey)
            );

            if (selectedRow) {
                selectedRows.push(selectedRow);
            }
        } else {
            selectedRows.splice(index, 1);
        }

        this.selectedRows.set(selectedRows);
        this.selectAll = selectedRows.length === this.rows.length;
        this.selectionChange.emit(selectedRows);
    }

    protected isSelected(row: User, selectAll: boolean): boolean {
        return (
            selectAll ||
            this.selectedRows().find(
                row_ => this.getRowValue(row_, this.selectionKey) === this.getRowValue(row, this.selectionKey)
            ) !== undefined
        );
    }

    protected invokeComponentInit(column: TableColumn<User>, row: User, component: unknown): void {
        if (column.componentInit) {
            column.componentInit(component, row);
        }
    }

    protected rowClicked(row: User): void {
        this.rowClick.emit(row);
    }
}
